import { useEffect, useState } from "react";
import { api } from "../../shared/api/client";
import type { ReleaseDetail, ReleaseDocumentType } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";

const DOCUMENT_TYPES: ReleaseDocumentType[] = [
  "RELEASE_NOTES", "DEPLOYMENT_PLAN", "TEST_PLAN", "TEST_REPORT", "UAT_REPORT",
  "ROLLBACK_PLAN", "DATABASE_SCRIPT", "MIGRATION_DOCUMENT", "ARCHITECTURE_DOCUMENT",
  "RUNBOOK", "OPERATIONAL_GUIDE", "APPROVAL_DOCUMENT", "CHANGE_REQUEST", "CAB_APPROVAL",
  "CUSTOMER_APPROVAL", "USER_GUIDE", "TRAINING_DOCUMENT", "KNOWN_ISSUES", "OTHER",
];

const readable = (type: string) => type.replace(/_/g, " ").toLowerCase();

/**
 * Saying a document is required, or recording one that lives elsewhere (§42).
 *
 * <p>Linking is a first-class way to satisfy a requirement rather than a fallback. Deployment
 * plans live in Confluence and CAB approvals in a change system; a release that could only be
 * satisfied by re-uploading a copy would either be permanently incomplete or accumulate stale
 * duplicates of documents that are maintained somewhere else.
 */
export function DocumentDrawer({
  release,
  mode,
  open,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  mode: "require" | "link";
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [documentType, setDocumentType] = useState<ReleaseDocumentType>("DEPLOYMENT_PLAN");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [mandatory, setMandatory] = useState(true);
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  useEffect(() => {
    if (!open) {
      return;
    }
    setDocumentType("DEPLOYMENT_PLAN");
    setTitle("");
    setUrl("");
    setNotes("");
    setMandatory(true);
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode]);

  const linking = mode === "link";
  const canSubmit = !linking || url.trim() !== "";

  const submit = () =>
    void run(
      () =>
        linking
          ? api.post(`/api/v1/releases/${release.id}/documents/link`, {
              documentType,
              url: url.trim(),
              title: title.trim() === "" ? null : title.trim(),
              notes: notes.trim() === "" ? null : notes.trim(),
            })
          : api.post(`/api/v1/releases/${release.id}/documents`, {
              documentType,
              title: title.trim() === "" ? null : title.trim(),
              mandatory,
              notes: notes.trim() === "" ? null : notes.trim(),
            }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={linking ? "Link a document" : "Require a document"}
      subtitle={
        linking
          ? "For a document that lives in a wiki or a change system."
          : "Something this release must produce before it can go."
      }
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit} loading={saving}>
            {saving ? "Saving…" : linking ? "Link" : "Require"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <Field label="Kind of document" required>
          <Select
            value={documentType}
            onChange={(event) => setDocumentType(event.target.value as ReleaseDocumentType)}
          >
            {DOCUMENT_TYPES.map((value) => (
              <option key={value} value={value}>
                {readable(value)}
              </option>
            ))}
          </Select>
        </Field>

        {linking && (
          <Field label="Link" required hint="Where the document actually lives.">
            <Input
              autoFocus
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              placeholder="https://wiki.example.com/deploy/2-1"
            />
          </Field>
        )}

        <Field label="Title" hint="Optional — the kind of document is usually enough.">
          <Input value={title} onChange={(event) => setTitle(event.target.value)} />
        </Field>

        {!linking && (
          <label className="flex items-start gap-2 text-sm text-ink-800">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-ink-300"
              checked={mandatory}
              onChange={(event) => setMandatory(event.target.checked)}
            />
            <span>
              The release cannot go without it
              <span className="block text-xs text-ink-500">
                A missing required document fails the readiness gate by name.
              </span>
            </span>
          </label>
        )}

        <Field label="Notes">
          <Textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} />
        </Field>
      </div>
    </Drawer>
  );
}
